import { useRoute } from '@react-navigation/native'
import React, { FC, useEffect } from 'react'
import { View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import styled, { useTheme } from 'styled-components/native'

import { AchievementEnum } from 'api/gen'
import { Achievement } from 'features/achievements/components/Achievement'
import {
  achievementCategoryDisplayTitles,
  achievementData,
} from 'features/achievements/data/AchievementData'
import { getAchievements } from 'features/achievements/helpers/getAchievements'
import { useAuthContext } from 'features/auth/context/AuthContext'
import { UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { ProgressBar } from 'ui/components/bars/ProgressBar'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { AccessibleIcon } from 'ui/svg/icons/types'
import { getSpacing, Typo } from 'ui/theme'
import { getTextSemanticAttrs } from 'ui/theme/typographyAttrs/getTextSemanticAttrs'

const emptyAchievement = {
  name: 'empty' as AchievementEnum,
  isCompleted: false,
  illustration: null as unknown as FC<AccessibleIcon>,
  title: '',
  description: '',
}

const Achievements = () => {
  const { params } = useRoute<UseRouteType<'Achievements'>>()
  const { user } = useAuthContext()
  const { categories, track } = getAchievements({
    achievements: achievementData,
    completedAchievements: user?.achievements || [],
  })
  const theme = useTheme()
  useEffect(() => {
    if (params?.from) {
      track(params.from)
    }
  }, [params?.from, track])

  return (
    <PageWithHeader
      title="Mes succès"
      scrollChildren={
        <ViewGap gap={4}>
          {categories.map((category) => {
            const isOddAchievements = category.achievements.length % 2 !== 0
            const achievements = isOddAchievements
              ? [...category.achievements, emptyAchievement]
              : category.achievements

            return (
              <ViewGap gap={4} key={category.name}>
                <AchievementsGroupeHeader>
                  <View>
                    <Typo.Title4 {...getTextSemanticAttrs(2)}>
                      {achievementCategoryDisplayTitles[category.name]}
                    </Typo.Title4>
                    <StyledBody>{category.remainingAchievementsText}</StyledBody>
                  </View>
                  <CompletionContainer>
                    <ProgressBarContainer>
                      <ProgressBar progress={category.progress} height={2.5} />
                    </ProgressBarContainer>
                    <Typo.BodyS>{category.progressText}</Typo.BodyS>
                  </CompletionContainer>
                </AchievementsGroupeHeader>
                <FlatList
                  data={achievements}
                  numColumns={2}
                  keyExtractor={(item) => item.name}
                  contentContainerStyle={{
                    gap: theme.designSystem.size.spacing.xl,
                    rowGap: theme.designSystem.size.spacing.xl,
                    paddingHorizontal: theme.designSystem.size.spacing.s,
                  }}
                  columnWrapperStyle={{
                    gap: theme.designSystem.size.spacing.xl,
                  }}
                  renderItem={({ item }) =>
                    item.illustration ? (
                      <Achievement
                        name={item.name}
                        title={item.title}
                        Illustration={item.illustration}
                        isCompleted={item.isCompleted}
                      />
                    ) : (
                      <EmptyAchievement accessible={false} />
                    )
                  }
                />
              </ViewGap>
            )
          })}
        </ViewGap>
      }
    />
  )
}

const EmptyAchievement = styled.View({
  flex: 0.5,
})

const StyledBody = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))

const AchievementsGroupeHeader = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const CompletionContainer = styled(ViewGap).attrs<{ gap?: number }>({ gap: 2 })({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
})

const ProgressBarContainer = styled.View({
  flex: 1,
  maxWidth: getSpacing(20),
})

export default Achievements
